import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, Eye, Headphones, Download, Zap } from 'lucide-react';
import { FormField } from '../../types/fal';
import { DynamicFormFields } from './DynamicFormFields';
import { CachedImage } from '../CachedImage';
import { getAssetMediaType } from '../../utils/formPreviewHelpers';
import { GeneratingVideoModal } from '../GeneratingVideoModal';
import { useAppStore } from '../../store/useAppStore';

interface MobileStepperFormProps {
  fields: FormField[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  creditCost?: number;
  disabled?: boolean;
  creditsChecked?: boolean;
  hasSufficientCredits?: boolean;
  currentCredits?: number | null;
  // Page content props
  pageTitle?: string;
  pageDescription?: string;
  exampleMediaUrl?: string;
  exampleMediaType?: string;
  // Generated assets for final step
  generatedAssets?: any[];
  onPreviewAsset?: (asset: any) => void;
  showSuccessStep?: boolean;
  showGeneratingModal?: boolean;
  onGeneratingComplete?: () => void;
  mediaType?: 'image' | 'video' | 'audio'; // Add mediaType prop
  onRestartForm?: () => void;
  onError?: (error: string) => void;
  appId?: string;
}

type StepType = 'intro' | 'field' | 'success';

interface Step {
  type: StepType;
  title: string;
  field?: FormField;
  fieldIndex?: number;
}

export const MobileStepperForm: React.FC<MobileStepperFormProps> = ({
  fields,
  values,
  onChange,
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Generate',
  creditCost = 10,
  disabled = false,
  creditsChecked = true,
  hasSufficientCredits = true,
  currentCredits = null,
  pageTitle,
  pageDescription,
  exampleMediaUrl,
  exampleMediaType,
  generatedAssets = [],
  onPreviewAsset,
  showSuccessStep = false,
  showGeneratingModal = true,
  onGeneratingComplete,
  mediaType,
  appId,
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const { activeModal, openInsufficientCreditsModalWithAmount, openErrorModal } = useAppStore();

  // Check if InsufficientCreditsModal is open
  const isInsufficientCreditsModalOpen = activeModal === 'insufficientCredits';

  // Filter out hidden fields for the stepper
  const visibleFields = fields.filter(field => !field.hidden);

  // Build steps array: intro + fields + success (if applicable)
  const steps: Step[] = [
    // Intro step (always first)
    {
      type: 'intro',
      title: pageTitle || 'Welcome'
    },
    // Field steps
    ...visibleFields.map((field, index) => ({
      type: 'field' as StepType,
      title: field.label,
      field,
      fieldIndex: index
    })),
    // Success step (if showing results)
    ...(showSuccessStep ? [{
      type: 'success' as StepType,
      title: 'Complete'
    }] : [])
  ];

  const totalSteps = steps.length;
  const currentStep = steps[currentStepIndex];
  const isIntroStep = currentStep?.type === 'intro';
  const isFieldStep = currentStep?.type === 'field';
  const isSuccessStep = currentStep?.type === 'success';
  const isLastFieldStep = isFieldStep && currentStepIndex === steps.length - (showSuccessStep ? 2 : 1);

  // Check if current field step is valid
  const isCurrentFieldValid = () => {
    if (!isFieldStep || !currentStep.field) return true;
    
    const fieldKey = currentStep.field.key || `field_${currentStep.fieldIndex}`;
    const value = values[fieldKey];
    
    if (currentStep.field.required) {
      if (value === undefined || value === null || value === '') {
        return false;
      }
      
      if (currentStep.field.type === 'file') {
        if (currentStep.field.multiple) {
          return Array.isArray(value) && value.length > 0;
        } else {
          return value !== null && value !== undefined;
        }
      }
    }
    
    return true;
  };

  const canProceed = () => {
    if (isIntroStep) return true;
    if (isFieldStep) return isCurrentFieldValid();
    if (isSuccessStep) return true;
    return false;
  };

  const handleNext = () => {
    if (isLastFieldStep && !showSuccessStep) {
      // Submit the form
      onSubmit();
    } else if (canProceed() && currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  // Auto-advance from intro step after a delay
  useEffect(() => {
    if (isIntroStep && exampleMediaUrl) {
      const timer = setTimeout(() => {
        if (currentStepIndex === 0) {
          setCurrentStepIndex(1);
        }
      }, 200000); // Auto-advance after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [isIntroStep, exampleMediaUrl, currentStepIndex]);

  // Handle generation completion
  useEffect(() => {
    if (showSuccessStep && generatedAssets.length > 0) {
      // Generation completed, move to success step
      setCurrentStepIndex(steps.length - 1);
      onGeneratingComplete?.();
    }
  }, [showSuccessStep, generatedAssets.length, steps.length, onGeneratingComplete]);
  const renderStepContent = () => {
    switch (currentStep.type) {
      case 'intro':
        return (
          <div className="flex-1 flex flex-col">
            {/* Example Media First */}
            {exampleMediaUrl && (
              <div className="flex-1 flex items-start justify-center p-4 pb-8">
                <div className="w-full max-w-sm mt-4">
                  {exampleMediaType === 'image' && (
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                      <img
                        src={exampleMediaUrl}
                        alt="Example output"
                        className="w-full h-auto object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="w-full h-64 flex items-center justify-center text-th-secondary bg-elevated rounded-2xl"><svg class="w-12 h-12" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg></div>';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4 text-center">
                        <p className="text-white font-medium">Example Output</p>
                      </div>
                    </div>
                  )}
                  
                  {exampleMediaType === 'video' && (
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                      <video
                        src={exampleMediaUrl}
                        className="w-full h-auto object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                        onError={(e) => {
                          const target = e.target as HTMLVideoElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="w-full h-64 flex items-center justify-center text-th-secondary bg-elevated rounded-2xl"><svg class="w-12 h-12" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div>';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4 text-center">
                        <p className="text-white font-medium">Example Output</p>
                      </div>
                    </div>
                  )}
                  
                  {exampleMediaType === 'audio' && (
                    <div className="bg-status-warning/20 rounded-2xl p-8 text-center space-y-6 shadow-2xl">
                      <div className="w-20 h-20 bg-status-warning rounded-full flex items-center justify-center mx-auto">
                        <Headphones className="w-10 h-10 text-white" />
                      </div>
                      <div>
                        <p className="text-th-primary font-semibold mb-4">Example Audio Output</p>
                        <audio
                          src={exampleMediaUrl}
                          controls
                          className="w-full bg-elevated rounded-lg"
                          onError={(e) => {
                            const target = e.target as HTMLAudioElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = '<div class="text-center text-th-secondary">Example audio unavailable</div>';
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Title and Description */}
            <div className="text-center space-y-4 px-6 pb-4">
              <h1 className="text-3xl font-bold text-th-primary">
                {pageTitle || 'AI Generator'}
              </h1>
              {pageDescription && (
                <p className="text-th-secondary text-lg leading-relaxed">
                  {pageDescription}
                </p>
              )}
              {creditsChecked && currentCredits !== null && (
                <p className="text-th-secondary text-sm mt-2">
                  Current Credits: <span className="font-semibold text-brand">{currentCredits}</span>
                </p>
              )}
            </div>

            {/* Get Started Message */}
            <div className="p-6 text-center">
              <p className="text-th-secondary mb-6">
                Ready to create amazing content? Let's get started!
              </p>
              <div className="text-xs text-th-secondary/70">
                Tap "Next" or swipe to continue
              </div>
            </div>
          </div>
        );

      case 'field':
        if (!currentStep.field) return null;
        
        return (
          <div className="flex-1 flex flex-col p-6">
            {/* Field Input - Centered */}
            <div className="flex-1 flex items-start justify-center pt-8">
              <div className="w-full max-w-md space-y-6">
                <DynamicFormFields
                  fields={[{
                    ...currentStep.field,
                    id: currentStep.field.key || `field_${currentStep.fieldIndex}`,
                    variable_name: currentStep.field.key || `field_${currentStep.fieldIndex}`,
                    required: currentStep.field.required || false
                  }]}
                  values={values}
                  onChange={onChange}
                  disabled={disabled || isSubmitting}
                  appId={appId}
                />
              </div>
            </div>

            {/* Field Status */}
            <div className="text-center mt-6">
              {currentStep.field.required && (
                <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm ${
                  isCurrentFieldValid()
                    ? 'bg-status-success/20 text-status-success'
                    : 'bg-status-warning/20 text-status-warning'
                }`}>
                  {isCurrentFieldValid() ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Complete</span>
                    </>
                  ) : (
                    <>
                      <span className="w-4 h-4 flex items-center justify-center font-bold">!</span>
                      <span>Required</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="flex-1 flex flex-col p-6">
            {/* Success Header */}
            <div className="text-center space-y-4 mb-8">
              <div className="w-20 h-20 bg-status-success rounded-full flex items-center justify-center mx-auto">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-th-primary">
                Generation Complete!
              </h2>
              <p className="text-th-secondary">
                Your content has been generated successfully
              </p>
            </div>

            {/* Generated Assets Preview */}
            {generatedAssets.length > 0 && (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-full max-w-md space-y-6">
                  {generatedAssets.slice(0, 3).map((asset, index) => (
                    <div key={asset.id || index} className="relative group">
                      <div 
                        className="bg-elevated rounded-2xl overflow-hidden shadow-lg cursor-pointer transform transition-all hover:scale-105"
                        onClick={() => onPreviewAsset?.(asset)}
                      >
                        {/* Asset Preview */}
                        <div className="relative">
                          {renderAssetPreview(asset)}
                          
                          {/* Preview Overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Asset Info */}
                        <div className="p-4">
                          <p className="text-th-primary font-medium truncate">
                            {asset.filename || `Generated ${getAssetMediaType(asset)}`}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-th-secondary text-sm">
                              {getAssetMediaType(asset).toUpperCase()}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = document.createElement('a');
                                link.href = asset.public_url;
                                link.download = asset.filename || `generated-${asset.id}`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className="flex items-center space-x-1 px-3 py-1 bg-brand/20 text-brand rounded-full text-xs font-medium hover:bg-brand/30 transition-all"
                            >
                              <Download className="w-3 h-3" />
                              <span>Save</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {generatedAssets.length > 3 && (
                    <div className="text-center">
                      <p className="text-th-secondary text-sm">
                        +{generatedAssets.length - 3} more assets generated
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const renderAssetPreview = (asset: any) => {
    const mediaType = getAssetMediaType(asset);
    
    switch (mediaType) {
      case 'image':
        return (
          <div className="aspect-square w-full">
            <CachedImage
              src={asset.public_url}
              alt="Generated content"
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full flex items-center justify-center text-th-secondary bg-elevated">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                  </svg>
                </div>
              }
            />
          </div>
        );
      
      case 'video':
        return (
          <div className="aspect-video w-full bg-elevated flex items-center justify-center relative">
            <video
              src={asset.public_url}
              className="w-full h-full object-cover"
              controls
              preload="metadata"
              onError={(e) => {
                const target = e.target as HTMLVideoElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<div class="w-full h-full flex items-center justify-center text-th-secondary bg-elevated"><svg class="w-12 h-12" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg></div>';
                }
              }}
            />
          </div>
        );
      
      case 'audio':
        return (
          <div className="aspect-square w-full bg-status-warning/20 flex items-center justify-center p-6">
            <div className="text-center space-y-4 w-full">
              <Headphones className="w-16 h-16 text-status-warning mx-auto" />
              <audio
                src={asset.public_url}
                controls
                className="w-full bg-elevated rounded-lg"
                onError={(e) => {
                  const target = e.target as HTMLAudioElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = '<div class="text-center text-th-secondary">Audio unavailable</div>';
                  }
                }}
              />
            </div>
          </div>
        );
      
      default:
        return (
          <div className="aspect-square w-full bg-elevated flex items-center justify-center">
            <Zap className="w-12 h-12 text-th-secondary" />
          </div>
        );
    }
  };

  return (
    <>
      {/* Generating Modal */}
      {isSubmitting && showGeneratingModal && (
        <GeneratingVideoModal 
          isOpen={isSubmitting}
          isGenerating={isSubmitting}
          onComplete={() => onGeneratingComplete?.()}
          mediaType={mediaType}
        />
      )}

      <div className="h-screen bg-base flex flex-col relative">
      {/* Step Content - Scrollable */}
      <div className="flex-1 overflow-y-auto pb-20">
        {renderStepContent()}
      </div>

      {/* Navigation Footer - Sticky */}
      <div className={`absolute bottom-0 left-0 right-0 bg-surface border-t border-subtle ${
        isInsufficientCreditsModalOpen ? 'hidden' : 'block'
      }`} style={{ 
        zIndex: 9999,
        display: isInsufficientCreditsModalOpen ? 'none' : 'block'
      }}>
        <div className="p-4">
          <div className="flex items-center justify-between space-x-4">
            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              disabled={currentStepIndex === 0 || isSubmitting}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all relative z-10 ${
                currentStepIndex === 0 || isSubmitting
                  ? 'bg-elevated/50 text-th-secondary/50 cursor-not-allowed'
                  : 'bg-elevated border border-subtle text-th-secondary hover:text-th-primary hover:border-brand/50'
              }`}
              style={{ zIndex: 10000, minHeight: '48px', minWidth: '48px' }}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back</span>
            </button>

            {/* Next/Submit Button */}
            {isSuccessStep ? (
              <button
                onClick={() => {
                  // Reload the entire URL for a fresh start
                  window.location.reload();
                }}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-brand text-surface rounded-xl font-medium hover:bg-brand/90 transition-all relative z-10 touch-manipulation"
                style={{ zIndex: 10000, minHeight: '48px', minWidth: '48px' }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 12a8 8 0 018-8V2.5"/>
                  <path d="M12 4a8 8 0 018 8h1.5"/>
                </svg>
                <span>Restart</span>
              </button>
            ) : isLastFieldStep && !showSuccessStep ? (
              <button
                onClick={handleNext}
                disabled={!canProceed() || isSubmitting || !creditsChecked || !hasSufficientCredits}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-brand text-surface rounded-xl font-medium hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand/30 relative z-10 touch-manipulation"
                style={{ minHeight: '48px', minWidth: '48px' }}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : !creditsChecked ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Checking Credits...</span>
                  </>
                ) : !hasSufficientCredits ? (
                  <>
                    <Zap className="w-4 h-4" />
                    <span>Insufficient Credits</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    <span>{submitLabel} ({creditCost} credits)</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleNext}
                disabled={!canProceed() || isSubmitting}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-brand text-surface rounded-xl font-medium hover:bg-brand/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-brand/30 relative z-10 touch-manipulation"
                style={{ minHeight: '48px', minWidth: '48px' }}
              >
                <span>Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* AI Disclaimer */}
          <div className="mt-3 text-center lg:block">
            <p className="text-xs text-th-secondary/70">
              AI may generate inaccurate results. Credits are non-refundable.
              {import.meta.env.MODE === 'development' && (
                <>
                  {' • '}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🧪 [DEV] Preview Credits Modal clicked');
                      console.log('🧪 [DEV] Opening insufficient credits modal...');
                      openInsufficientCreditsModalWithAmount(creditCost);
                    }}
                    className="text-status-warning underline hover:text-status-warning/80 transition-colors"
                  >
                    Preview Credits Modal
                  </button>
                  {' • '}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('🧪 [DEV] Preview Error Modal clicked');
                      openErrorModal('Generation Failed', 'Copyright Material Detected: Please try again with different input/image.');
                    }}
                    className="text-status-error underline hover:text-status-error/80 transition-colors"
                  >
                    Preview Error Modal
                  </button>
                </>
              )}
            </p>
          </div>

          {/* Step Info */}
          {isFieldStep && (
            <div className="mt-3 text-center">
              <p className="text-xs text-th-secondary">
                {visibleFields.filter((_, index) => {
                  const stepIndex = index + 1; // +1 because intro step is at index 0
                  return stepIndex < currentStepIndex;
                }).length} of {visibleFields.length} fields completed
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};